import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // Android cierra un <dialog> modal con el boton/gesto de atras de forma NATIVA,
    // sin pasar por closeDialog(), asi que unlockScroll() nunca corria y la pagina
    // quedaba congelada con overflow:hidden y ningun dialogo abierto. El evento
    // nativo 'close' cubre TODAS las vias de cierre. unlockScroll es Set.delete +
    // sync: idempotente, llamarlo de mas no tiene efecto. (31/08/2026)
    this.refs.dialog?.addEventListener('close', this.#handleNativeClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.refs.dialog?.removeEventListener('close', this.#handleNativeClose);
    this.#closingInternally = false;
    unlockScroll(this.refs.dialog);
  }

  /**
   * True mientras closeDialog() maneja el cierre. Lo CONSUME el handler nativo: cada
   * dialog.close() del componente produce exactamente un evento `close`, asi que la
   * bandera se apaga sola en el mismo ciclo y no se queda pegada.
   */
  #closingInternally = false;

  /**
   * Suelta el scroll pase lo que pase: cierre nativo, Escape del navegador o boton atras.
   *
   * Y despacha DialogCloseEvent cuando el cierre NO vino de closeDialog(). De ese evento
   * cuelgan media.js (pausar el video), drag-zoom-wrapper (resetear el zoom), quick-add,
   * predictive-search y anchored-popover: si nadie lo despacha, esas limpiezas no corren.
   * Se prefiere el riesgo de despacharlo dos veces (todos esos listeners son idempotentes)
   * al de no despacharlo nunca.
   */
  #handleNativeClose = () => {
    const dialog = this.refs.dialog;

    // La bandera se consume SIEMPRE, incluso si salimos por la guarda de abajo: cada
    // dialog.close() del componente produce un solo evento `close`, y dejarla puesta
    // envenenaria el proximo cierre nativo.
    const eraInterno = this.#closingInternally;
    this.#closingInternally = false;

    // Cerrado y reabierto en el mismo bloque sincrono — pasa cuando este dialogo esta
    // anidado dentro de un theme-drawer y el drawer cambia de modo al cruzar los 990px
    // (#bringNestedDialogToFront hace close() + showModal() seguidos). El evento `close`
    // se encola como tarea, asi que cuando llegamos aca el dialogo ya esta abierto otra
    // vez: soltarle el candado lo dejaria visible con la pagina de atras scrolleando.
    if (dialog?.open) return;

    unlockScroll(dialog);
    dialog?.classList.remove('dialog-closing');

    if (eraInterno) return;

    this.dispatchEvent(new DialogCloseEvent());
  };

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    // Marcar solo si de verdad hay un cierre por delante: si el dialogo ya se cerro por otra
    // via (el boton atras durante la animacion), close() no dispara evento y la bandera
    // quedaria puesta envenenando el proximo cierre nativo.
    if (dialog.open) this.#closingInternally = true;
    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
